import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function InquiryBasedLearningPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
      isActive: true,
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Inquiry-based learning"
        description="Inquiry-based learning (IBL) involves students exploring questions, problems, or phenomena through investigation and discovery. Guided by teachers, students construct explanations and develop critical thinking by asking questions, doing research and conducting investigations."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'US philosopher John Dewey, in early 1900s.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                <Link
                  url="https://www.mckinsey.com/industries/social-sector/our-insights/how-to-improve-student-educational-outcomes-new-insights-from-data-analytics"
                  openInNewTab
                >
                  This 2017 McKinsey research
                </Link>{' '}
                found that students performed best in PISA assessments, 26 points above the
                baseline, when students had learnt with a combination of Inquiry-Based Learning and
                Direct Instruction.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <>
                <Link
                  url="https://nap.nationalacademies.org/catalog/11102/how-students-learn-science-in-the-classroom"
                  openInNewTab
                >
                  How Students Learn: Science in the Classroom
                </Link>
                , by the US National Research Council.
              </>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Balanced alongside an explicit teaching approach. Stile units incorporate Inquiry-based approaches through Questions Boards, hands-on labs, investigations and Engineering Challenges.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="understanding-via-observation"
            title="Understanding via observation"
            description="Students explore types of energy by making observations of objects in a mystery box. This lesson is purposefully placed before an explicit teaching lesson on types of energy to spark curiosity and create cognitive dissonance before supporting students to understand their observations."
            url="http://stileapp.com/go/inquiry-example-1"
            subtitle={
              <div>
                <em>Energy mystery box</em> lesson
                <br />
                in the Energy unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/inquiry_observation.png"
                alt="Text: Classify the items based on similarities that you can observe. Image: Box with assorted items such as flashlights, batteries, apple, spoon, rubber duck, and leaves."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="investigating-problems"
            title="Investigating problems"
            description="Students investigate data collected from multiple lakes to determine the water quality."
            url="http://stileapp.com/go/inquiry-example-2"
            subtitle={
              <div>
                <em>Water quality and potability</em> lesson
                <br />
                in the Hydrosphere unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/inquiry_investigate.png"
                alt="Map showing lakes with a data table of a lake's water quality indicators including temperature, dissolved oxygen, pH, nitrates, phosphates, turbidity, chlorophyll, and bioindicators."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="solving-mysteries"
            title="Solving mysteries through research"
            description="Students progressively build their understanding through investigations and research, culminating in the ability to classify a mystery gem."
            url="http://stileapp.com/go/inquiry-example-3"
            subtitle={
              <div>
                <em>Minerals</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/inquiry_mystery.png"
                alt="Text: Classify the mystery green mineral on Hawaii’s Papakōlea beach by investigating its key properties. Image: Green mineral with labels for cleavage, streak, color, hardness, and luster, shown against the beach background."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default InquiryBasedLearningPage;
